import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  apiCallFinished,
  envConfigSelectors,
  errorMessagesActions,
  PartnersIntro,
  SelectablePartnerList,
  StateContext,
  states,
  warningModalSelectors,
  withProgressBar,
  withRestorableDraft,
} from '~/components';
import { appointmentDraftActions, journeyStateActions } from '~/api';
import { loadPartners } from '~/routes/partners/partners.actions';
import {
  saveTemporaryAppointment,
  saveTemporaryJourneyState,
  setEditMode,
} from '../../edit-appointment.actions';
import { isNotEmpty } from '~/utils';

import { EditButtonsWrapper } from '../../components/edit-buttons-wrapper';
import { formatToTemporaryState } from '../../helpers';

import styles from './EditPartner.scss';
import {
  getTokenFromStorage,
  setTokenToStorage,
  TEMPORARY_TOKEN_NAME,
} from '~/components/persist-token/persist-token.utils';

export class EditPartner extends PureComponent {
  state = {
    isAjaxCompleted: false,
  };

  componentDidMount() {
    const {
      dispatchLoadPartners,
      dispatchApiCallFinished,
      temporaryAppointment,
      dispatchSaveTemporaryAppointment,
      dispatchSaveTemporaryJourneyState,
      draft,
      journeyState,
    } = this.props;

    dispatchApiCallFinished(false);
    if (isNotEmpty(temporaryAppointment)) {
      dispatchLoadPartners().then(() => {
        this.setState({ isAjaxCompleted: true });
        dispatchApiCallFinished(true);
      });
    } else {
      dispatchSaveTemporaryAppointment(formatToTemporaryState(draft));
      setTokenToStorage(getTokenFromStorage(), TEMPORARY_TOKEN_NAME);
      dispatchSaveTemporaryJourneyState(journeyState);
      setTokenToStorage(getTokenFromStorage(), TEMPORARY_TOKEN_NAME);
      dispatchLoadPartners().then(() => {
        this.setState({ isAjaxCompleted: true });
        dispatchApiCallFinished(true);
      });
    }
    window.scrollTo(0, 0);
  }

  selectPartner = (partner) => {
    const { dispatchApiCallFinished, dispatchSelectPartner, dispatchSavePartner, history } =
      this.props;

    dispatchApiCallFinished(false);
    dispatchSelectPartner(partner);
    dispatchSavePartner().then(() => {
      dispatchApiCallFinished(true);
      history.pushWithToken('edit-date');
    });
  };

  render() {
    const {
      draft,
      journeyState,
      partners,
      config: { partnerTitle, partnerPrefix, PartnersIntro: partnersIntroConfig },
      history,
      temporaryAppointment,
      temporaryJourneyState,
      dispatchSetEditMode,
      dispatchSetDraftAppointment,
      dispatchSaveTemporaryAppointment,
      dispatchSetJourneyState,
      prevPath,
      editMode,
      location,
      showWarningModal,
      hideGoBackButton,
    } = this.props;

    const { isAjaxCompleted } = this.state;

    const {
      shop: { name: selectedShopName },
      id: bookingId,
    } = draft;

    return (
      <section data-testid="Partners" data-test-ajax={isAjaxCompleted}>
        <div className={styles.EditPartnerContainer}>
          <PartnersIntro
            partnerTitle={partnerTitle}
            shopName={selectedShopName}
            partnerName={partners.length === 1 ? partners[0].name : null}
            {...partnersIntroConfig}
          />
          <div className={styles.PartnerListSection}>
            <SelectablePartnerList
              partners={partners}
              selectPartner={this.selectPartner}
              partnerTitle={partnerTitle}
              partnerPrefix={partnerPrefix}
              editMode={editMode}
              {...partnersIntroConfig}
            />
          </div>
        </div>
        <StateContext.Provider value={states.edit}>
          <EditButtonsWrapper
            hideGoBackButton={hideGoBackButton}
            temporaryDraftAppointment={temporaryAppointment}
            history={history}
            location={location}
            showWarningModal={showWarningModal}
            bookingId={bookingId}
            state={formatToTemporaryState(draft)}
            journeyState={journeyState}
            setEditMode={dispatchSetEditMode}
            saveTemporaryState={dispatchSaveTemporaryAppointment}
            prevPath={prevPath}
            temporaryJourneyState={temporaryJourneyState}
            restoreAppointmentDraft={dispatchSetDraftAppointment}
            restoreJourneyState={dispatchSetJourneyState}
          />
        </StateContext.Provider>
      </section>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    appointment: { draft },
    journeyState,
    partners: { partners },
    services: { config },
    editAppointment: { temporaryAppointment, editMode, temporaryJourneyState },
    router: { location },
  } = state;
  const warningModalState = warningModalSelectors.selectState(state);
  const firstEditPagePath = envConfigSelectors.selectFirstEditPage(draft.appointmentType.id)(state);
  return {
    draft,
    journeyState,
    partners,
    config,
    temporaryAppointment,
    temporaryJourneyState,
    editMode,
    prevPath: config.editPartnerPrevPath,
    location,
    showWarningModal: !!warningModalState.isOpen,
    hideGoBackButton: location.pathname.indexOf(firstEditPagePath) !== -1,
  };
};

const mapDispatchToProps = {
  dispatchApiCallFinished: apiCallFinished,
  dispatchLoadPartners: loadPartners,
  dispatchSelectPartner: appointmentDraftActions.setPartner,
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchSetEditMode: setEditMode,
  dispatchSaveTemporaryAppointment: saveTemporaryAppointment,
  dispatchSaveTemporaryJourneyState: saveTemporaryJourneyState,
  dispatchSetDraftAppointment: appointmentDraftActions.setDraftAppointment,
  dispatchSavePartner: appointmentDraftActions.saveAppointmentDraft,
  dispatchSetJourneyState: journeyStateActions.setJourneyState,
};

// remove this from here use this in Route.
export const ConnectedEditPartner = withRestorableDraft(
  connect(mapStateToProps, mapDispatchToProps)(withProgressBar(EditPartner)),
);

export const ConnectedEditPartnerForManageAppointment = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditPartner);

EditPartner.propTypes = {
  history: PropTypes.shape({}).isRequired,
  prevPath: PropTypes.string.isRequired,
  partners: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  config: PropTypes.shape({
    partnerTitle: PropTypes.string.isRequired,
    partnerPrefix: PropTypes.string.isRequired,
    PartnersIntro: PropTypes.shape({}).isRequired,
  }).isRequired,
  draft: PropTypes.shape({
    shop: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    partner: PropTypes.shape({}).isRequired,
    id: PropTypes.string,
  }).isRequired,
  temporaryAppointment: PropTypes.shape({}).isRequired,
  editMode: PropTypes.bool.isRequired,
  hideGoBackButton: PropTypes.bool.isRequired,
  temporaryJourneyState: PropTypes.shape({}).isRequired,
  journeyState: PropTypes.shape({}).isRequired,
  showWarningModal: PropTypes.bool.isRequired,
  location: PropTypes.shape({}).isRequired,

  dispatchLoadPartners: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchSelectPartner: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchSetEditMode: PropTypes.func.isRequired,
  dispatchSaveTemporaryAppointment: PropTypes.func.isRequired,
  dispatchSaveTemporaryJourneyState: PropTypes.func.isRequired,
  dispatchSetDraftAppointment: PropTypes.func.isRequired,
  dispatchSavePartner: PropTypes.func.isRequired,
  dispatchSetJourneyState: PropTypes.func.isRequired,
};
